import { team, teamMemberAt } from "../../data/team";
import { blocksBefore } from "../../lib/content-blocks";
import { localHref } from "../../lib/links";

// The live profile template's section headings, in its order.
const sectionHeading =
  /^(role|career|fuel|fun fact|special skill|secret weapon)$/i;
// Short one-liners that read best as cards beside the story.
const factHeading = /^(fun fact|special skill|secret weapon)$/i;

const titleCase = (text = "") =>
  text.toLowerCase().replace(/(^|\s)(\p{L})/gu, (m) => m.toUpperCase());

const linkedInKey = (href = "") =>
  href.replace(/^http:/, "https:").replace(/\/?$/, "/");

/**
 * The profile's own LinkedIn URL. Captured pages repeat it, and some carry a
 * teammate's link left over from a copied page, so links that belong to
 * someone else on the roster are ignored and the most frequent one wins.
 */
function ownLinkedIn(links = []) {
  const others = new Set(
    team.map((p) => p.linkedin && linkedInKey(p.linkedin)),
  );
  const counts = new Map();
  for (const { href } of links) {
    const key = linkedInKey(href);
    if (!others.has(key)) counts.set(key, (counts.get(key) || 0) + 1);
  }
  let best;
  for (const [href, n] of counts) if (!best || n >= best.n) best = { href, n };
  return best?.href;
}

/** Captured "Role / Career / Fuel / Fun Fact…" sections as { heading, html }. */
function capturedSections(blocks) {
  const sections = [];
  for (const block of blocks) {
    if (/^h\d$/.test(block.type) && sectionHeading.test(block.text?.trim()))
      sections.push({ heading: titleCase(block.text.trim()), html: "" });
    else if (block.type === "rich" && sections.length)
      sections[sections.length - 1].html += block.html;
  }
  return sections.filter((s) => s.html.replace(/<[^>]*>|\s/g, ""));
}

/**
 * Everything a team profile page shows, merging the roster entry in
 * data/team.js (name, title, photo, new bio) over the captured live page.
 * People no longer on the roster fall back to the captured page alone.
 */
export function teamProfile(page, path) {
  const member = teamMemberAt(path);
  const blocks = blocksBefore(page?.body || [], /Other Team Members/);
  const headings = blocks.filter((b) => /^h[12]$/.test(b.type));
  const capturedName = page?.heading || headings[0]?.text || page?.title || "";
  const firstSection = blocks.findIndex((b) =>
    sectionHeading.test(b.text?.trim() || ""),
  );
  const capturedRole = blocks
    .slice(0, firstSection < 0 ? undefined : firstSection)
    .filter((b) => /^h[12]$/.test(b.type) && b.text !== capturedName)
    .at(-1)?.text;
  const capturedPhoto = blocks.find((b) => b.type === "image")?.src;

  const fullName = member?.fullName || capturedName.trim();
  const sections = member?.bio?.length
    ? member.bio.map(({ heading, text }) => ({ heading, text }))
    : capturedSections(blocks);

  return {
    path,
    member,
    name: member?.name || fullName.split(" ")[0],
    fullName,
    title: member?.title || titleCase(capturedRole?.trim()),
    group: member?.group,
    photo: member?.photo,
    photoUrl: !member?.photo && capturedPhoto ? localHref(capturedPhoto) : "",
    linkedin: member?.linkedin || ownLinkedIn(page?.socialLinks),
    story: sections.filter((s) => !factHeading.test(s.heading)),
    facts: sections.filter((s) => factHeading.test(s.heading)),
  };
}

/** Up to `count` other roster members, starting after this person. */
export function moreTeam(path, count = 4) {
  const index = team.findIndex((p) => p.path === path);
  const rotated = [...team.slice(index + 1), ...team.slice(0, index + 1)];
  return rotated.filter((p) => p.path !== path).slice(0, count);
}
